import { Component, EventEmitter, Output } from '@angular/core';
import { BaseComponent } from '../moov-common/base/base.component';
import { NavigationEnd, Router } from '@angular/router';
import { NAVIGATION_ROUTES } from '../navigation-routes.const';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AbstractPersonalUserDataService } from '../moov-common/services/personal-user-data/personal-user-data.abstract.service';
import { AccountTypeEnum } from '../moov-common/services/personal-user-data/personal-user-data';
import { FeatureID } from '../moov-common/enum/feature.enum';
import { InternalEventsManagerService } from '../moov-common/services/internal-events-manager/internal-events-manager.service';
import { AbstractStructuredProcessService } from '../moov-common/services/chat/structured-process.abstract.service';
import { ResponsesAbstractService } from '../moov-common/services/responses/responses.abstract.service';
import { StructuredProcessStateService } from '../moov-common/services/chat/structured-process-state.service';

@Component({
  selector: 'app-navigation-sidebar',
  templateUrl: './navigation-sidebar.component.html',
  styleUrl: './navigation-sidebar.component.scss',
})
export class NavigationSidebarComponent extends BaseComponent {
  isInLoginPage = false;
  isInTokenLoginPage = false;
  isInUnsubscribePage = false;
  private destroy$ = new Subject<void>();
  personalUserDataService: AbstractPersonalUserDataService;
  isLoggedIn: boolean = false;
  isChatAllowed: boolean = false;
  isTeamStateDashboardAllowed: boolean = false;
  isTeamViewAllowed: boolean = false;
  shouldPresentAdminPage: boolean = false;
  shouldPresentPulsePage: boolean = true;
  isStructuredProcessAllowed: boolean = false;
  isB2C: boolean = false;
  hasPendingPulseCheck: boolean = false;
  pendingPulseCheckToken: string | null = null;
  isOnProcessRoute: boolean = false;
  isOnPersonalView: boolean = false;
  isProcessActive: boolean = false;
  @Output() toggleMenuEvent = new EventEmitter<boolean>();

  constructor(
    public router: Router,
    personalUserDataService: AbstractPersonalUserDataService,
    private internalEvents: InternalEventsManagerService,
    private structuredProcessService: AbstractStructuredProcessService,
    private responsesService: ResponsesAbstractService,
    public processStateService: StructuredProcessStateService
  ) {
    super();
    this.personalUserDataService = personalUserDataService;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.isLoggedIn = this.user ? true : false;
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.verifyCurrentRoute();
      });

    this.verifyCurrentRoute();
    this.verifyFeatures();
    this.verifyUserOptions();

    this.processStateService.isProcessActive$
      .pipe(takeUntil(this.destroy$))
      .subscribe((active) => {
        this.isProcessActive = active;
      });
    this.internalEvents.userLoggedIn.subscribe(() => {
      this.isLoggedIn = this.user ? true : false;
      this.verifyFeatures();
      this.checkPendingPulseCheck();
    });

    this.internalEvents.pulseCheckCompleted.subscribe(() => {
      this.hasPendingPulseCheck = false;
      this.pendingPulseCheckToken = null;
    });

    this.checkPendingPulseCheck();
  }

  toggleMenu(opened: boolean): void {
    this.toggleMenuEvent.emit(opened);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  verifyFeatures(): void {
    if (!this.isLoggedIn) {
      // console.log(`in verifyFeatures`);
      this.isChatAllowed = false;
      this.isTeamStateDashboardAllowed = false;
      return;
    }

    this.personalUserDataService
      .verifyFeature(FeatureID.llmChat)
      .subscribe((isLLMChatAllowed) => {
        // console.log(`isChatAllowed is ${isFeaturesAllowed}`);
        this.isChatAllowed = isLLMChatAllowed;
      });

    this.personalUserDataService
      .verifyFeature(FeatureID.stateViewDashboard)
      .subscribe((isStateViewDashboardAllowed) => {
        // console.log(`isTeamStateDashboardAllowed is ${isFeaturesAllowed}`);
        this.isTeamStateDashboardAllowed = isStateViewDashboardAllowed;
      });

    this.personalUserDataService
      .verifyFeature(FeatureID.teamView)
      .subscribe((isTeamViewAllowed) => {
        this.isTeamViewAllowed = isTeamViewAllowed;
      });

    this.personalUserDataService
      .verifyFeature(FeatureID.structuredProcess)
      .subscribe((isStructuredProcessAllowed) => {
        this.isStructuredProcessAllowed = isStructuredProcessAllowed;
        if (isStructuredProcessAllowed) {
          const processId = this.user?.accountType === AccountTypeEnum.CAREER_INDEPENDENT
            ? 'SP_7b71514b296d'
            : this.user?.accountType === AccountTypeEnum.CAREER_INDEPENDENT_US
              ? 'SP_a4f9c832e17b'
              : '';
          this.structuredProcessService.getStructuredProcess(processId).subscribe((process) => {
            this.isB2C = process.id === 'SP_7b71514b296d' || process.id === 'SP_a4f9c832e17b';
          });
        }
      });
  }

  verifyUserOptions(): void {
    this.shouldPresentAdminPage = this.isAdminRole;
  }

  checkPendingPulseCheck(): void {
    if (!this.isLoggedIn) return;
    this.responsesService.getPendingPulseCheck().subscribe((data) => {
      this.hasPendingPulseCheck = data.hasPendingPulseCheck;
      this.pendingPulseCheckToken = data.token;
    });
  }

  navigateToPulseCheck(): void {
    this.toggleMenu(false);
    this.router.navigate(['/pulse'], { queryParams: { token: this.pendingPulseCheckToken } });
  }

  private verifyCurrentRoute() {
    const currentRoute = this.router.url;
    this.isInLoginPage = currentRoute === '/' + NAVIGATION_ROUTES.login;
    this.isInTokenLoginPage =
      currentRoute === '/' + NAVIGATION_ROUTES.tokenLogin;
    this.isInUnsubscribePage = currentRoute.startsWith(
      '/' + NAVIGATION_ROUTES.unsubscribe
    );
    this.isOnPersonalView = currentRoute.startsWith('/personalView');
    this.isOnProcessRoute = currentRoute.startsWith('/process')
      || (this.isB2C && this.isOnPersonalView);
  }

  onSidebarStepClicked(): void {
    this.toggleMenu(false);
  }
}
